import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BlogForm, { type BlogFormErrors, type BlogFormState } from '@/components/blog/blog-form';
import { toCategoryScope } from '@/components/blog/category-scope';
import type { SidebarConfig } from '@/config/sidebarConfig';
import {
  type CreateArticlePayload,
  createArticleApi,
  getArticleCategoriesApi,
  uploadArticleCoverApi,
} from '@/lib/api/blogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { ArticleStatus } from '@shared/enums/domain.enum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

const BaseArticleSchema = z.object({
  title: z.string().min(3, 'Minimum 3 caracteres').max(200),
  slug: z.string().max(200).optional(),
  excerpt: z.string().min(10, 'Minimum 10 caracteres'),
  content: z.string().min(20, 'Le contenu est trop court'),
  categoryId: z.number().int().positive('Selectionnez une categorie'),
  status: z.enum([ArticleStatus.DRAFT, ArticleStatus.PUBLISHED]),
  readTimeMinutes: z.number().int().min(1).max(120).optional(),
  tags: z.array(z.string()).optional(),
});
const FeaturedArticleSchema = BaseArticleSchema.extend({ featured: z.boolean() });

interface ScopedBlogCreateProps {
  sidebarConfig: SidebarConfig;
  listRoute: string;
  /** Extra fields injected into create payload (e.g. { organizationId: 5 }) */
  scopePayload?: Record<string, number>;
  /**
   * Force l'affichage du gestionnaire de categories.
   * Lot A bis : inutile pour les vues scopees — des qu'un scopePayload est
   * present, le client possede sa propre taxonomie et le bouton apparait seul.
   */
  showCategoryManager?: boolean;
  /** Show the "featured" checkbox (admin only) */
  showFeatured?: boolean;
}

export default function ScopedBlogCreate({
  sidebarConfig,
  listRoute,
  scopePayload,
  showCategoryManager = false,
  showFeatured = false,
}: ScopedBlogCreateProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const [form, setForm] = useState<BlogFormState>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    categoryId: 0,
    status: ArticleStatus.DRAFT,
    featured: false,
    readTimeMinutes: '',
    tagsInput: '',
  });
  const [errors, setErrors] = useState<BlogFormErrors>({});
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  // Lot A bis : la taxonomie suit le blog edite (club / organisation /
  // plateforme). Sans ce scope, un club Pro se verrait proposer les categories
  // editoriales de BoulesConnect alors qu'il paie pour son propre blog.
  const categoryScope = toCategoryScope(scopePayload);

  // Un client qui possede sa propre taxonomie doit pouvoir l'administrer. On le
  // deduit du scope plutot que d'aller activer la prop dans les 4 vues
  // club/orga : aucun oubli possible.
  const canManageCategories = showCategoryManager || categoryScope !== undefined;

  const { data: categories } = useQuery({
    queryKey: queryKeys.blog.categories(categoryScope),
    queryFn: () => getArticleCategoriesApi(categoryScope),
    enabled: isAuthenticated,
  });

  function validate(): CreateArticlePayload | null {
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt.trim(),
      content: form.content,
      categoryId: form.categoryId,
      status: form.status,
      featured: form.featured,
      readTimeMinutes: form.readTimeMinutes ? Number(form.readTimeMinutes) : undefined,
      tags: form.tagsInput
        ? form.tagsInput
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
        : undefined,
      ...scopePayload,
    };

    const schema = showFeatured ? FeaturedArticleSchema : BaseArticleSchema;
    const result = schema.safeParse(payload);
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const newErrors: BlogFormErrors = {};
      for (const [k, msgs] of Object.entries(fieldErrors)) {
        if (msgs?.[0]) newErrors[k as keyof BlogFormErrors] = msgs[0];
      }
      setErrors(newErrors);
      toast.error('Corrigez les erreurs du formulaire');
      return null;
    }
    setErrors({});
    return payload as CreateArticlePayload;
  }

  const createMutation = useMutation({
    mutationFn: async (payload: CreateArticlePayload) => {
      const article = await createArticleApi(payload);
      if (coverFile) await uploadArticleCoverApi(article.id, coverFile);
      return article;
    },
    onSuccess: () => {
      toast.success('Article cree');
      void queryClient.invalidateQueries({ queryKey: queryKeys.blog.all() });
      navigate(listRoute);
    },
    onError: () => toast.error("Erreur lors de la creation de l'article"),
  });

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  function handleCoverRemove() {
    setCoverFile(null);
    setCoverPreview(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = validate();
    if (payload) createMutation.mutate(payload);
  }

  function setField<K extends keyof BlogFormState>(key: K, value: BlogFormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  return (
    <BlogForm
      mode='create'
      sidebarConfig={sidebarConfig}
      listRoute={listRoute}
      form={form}
      errors={errors}
      onFieldChange={setField}
      onSubmit={handleSubmit}
      isPending={createMutation.isPending}
      categories={categories}
      showCategoryManager={canManageCategories}
      categoryScope={categoryScope}
      showFeatured={showFeatured}
      coverPreview={coverPreview}
      coverFileName={coverFile?.name}
      onCoverChange={handleCoverChange}
      onCoverRemove={handleCoverRemove}
    />
  );
}
